import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Building2, ArrowLeft, Copy, CheckCircle2, Users } from 'lucide-react';

const CompanyRegister = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [companyCode, setCompanyCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    companyCode: '',
    companyType: 'subcontractor', // 'gc' or 'subcontractor'
    contactEmail: '',
    contactName: '',
    address: '',
    phone: '',
    adminEmail: '',
    adminPassword: '',
    adminName: ''
  });

  // Navigate when user state updates after company registration login
  useEffect(() => {
    if (user && pendingNavigation) {
      navigate('/admin', { replace: true });
      setPendingNavigation(false);
    }
  }, [user, pendingNavigation, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosInstance.post('/companies/register', formData);
      
      console.log('Registration response:', response.data);
      console.log('Company code:', response.data.company.companyCode);
      
      // Store registration data for later use
      setRegistrationData(response.data);
      
      // Show success dialog with company code
      const code = response.data.company.companyCode;
      setCompanyCode(code);
      
      // Show success message - disable dialog, just use banner
      toast.success(`Company registered! Your code: ${code}`, {
        duration: 15000,
      });
      
      // Scroll to top to show banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(companyCode);
    setCopied(true);
    toast.success('Company code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleContinue = async () => {
    if (registrationData) {
      setShowSuccessDialog(false);
      // Store auth data and force hard redirect
      await login(registrationData.user, registrationData.token);
      window.location.href = '/admin';
    }
  };


  // Fix z-index for dialog overlay when it opens
  useEffect(() => {
    if (showSuccessDialog) {
      // Find and update all dialog overlays
      const overlays = document.querySelectorAll('[data-radix-dialog-overlay]');
      overlays.forEach(overlay => {
        overlay.style.zIndex = '99998';
      });
      
      // Find and update dialog content
      const contents = document.querySelectorAll('[data-radix-dialog-content]');
      contents.forEach(content => {
        content.style.zIndex = '99999';
      });
    }
  }, [showSuccessDialog]);


  return (
    <>
      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog} modal={true}>
        <DialogContent 
          className="sm:max-w-md" 
          style={{ zIndex: 99999 }}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl">Company Registered Successfully!</DialogTitle>
            <DialogDescription className="text-center">
              Your company has been registered. Share this code with your employees to join your company.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 rounded-lg p-6">
              <Label className="text-sm font-medium text-slate-600 block mb-2 text-center">
                Your Company Code
              </Label>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl font-bold text-orange-600 tracking-wider font-mono">
                  {companyCode}
                </span>
                <Button
                  onClick={handleCopyCode}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <strong>✉️ Email Sent!</strong> A confirmation email with your company code and registration details has been sent to your admin email address.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Important:</strong> Employees will need this code to register and join your company. 
                You can also find this code in your admin dashboard settings.
              </p>
            </div>

            <Button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
            >
              Continue to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Company Registration</CardTitle>
                <CardDescription>Register your company for SafetyMind AI training</CardDescription>
              </div>
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Company Information</h3>
              
              <div>
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Acme Construction Inc."
                />
              </div>

              <div>
                <Label htmlFor="companyCode">Company Code *</Label>
                <Input
                  id="companyCode"
                  name="companyCode"
                  value={formData.companyCode}
                  onChange={(e) => {
                    // Convert to uppercase and remove invalid characters
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
                    handleChange({ target: { name: 'companyCode', value } });
                  }}
                  required
                  placeholder="ACME2025"
                  maxLength={20}
                  className="uppercase"
                />
                <p className="text-xs text-slate-500 mt-1">
                  3-20 characters. Letters, numbers, hyphens, and underscores only. This code will be used by employees to join your company.
                </p>
              </div>

              {/* General Contractor Option */}
              <div className="space-y-3">
                <div 
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.companyType === 'gc' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData({ 
                    ...formData, 
                    companyType: formData.companyType === 'gc' ? 'subcontractor' : 'gc' 
                  })}
                >
                  <input
                    type="checkbox"
                    checked={formData.companyType === 'gc'}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      companyType: e.target.checked ? 'gc' : 'subcontractor' 
                    })}
                    className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <div className="ml-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold">Register as General Contractor</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Enable GC features to manage projects, link subcontractors, and oversee safety compliance across your projects.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contactName">Contact Person *</Label>
                  <Input
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <Label htmlFor="contactEmail">Contact Email *</Label>
                  <Input
                    id="contactEmail"
                    name="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    required
                    placeholder="contact@company.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Admin Account */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Company Administrator Account</h3>
              
              <div>
                <Label htmlFor="adminName">Admin Name *</Label>
                <Input
                  id="adminName"
                  name="adminName"
                  value={formData.adminName}
                  onChange={handleChange}
                  required
                  placeholder="Jane Smith"
                />
              </div>

              <div>
                <Label htmlFor="adminEmail">Admin Email *</Label>
                <Input
                  id="adminEmail"
                  name="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={handleChange}
                  required
                  placeholder="admin@company.com"
                />
              </div>

              <div>
                <Label htmlFor="adminPassword">Admin Password *</Label>
                <Input
                  id="adminPassword"
                  name="adminPassword"
                  type="password"
                  value={formData.adminPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Minimum 6 characters"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register Company'}
            </Button>
          </form>
        </CardContent>
      </Card>
      
      {/* Success Banner - Primary display for company code */}
      {companyCode && (
        <Card className="mt-6 border-4 border-green-500 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">
                Registration Successful!
              </h2>
              <p className="text-lg mb-6">Your company code is:</p>
              <div className="bg-white border-4 border-orange-500 rounded-lg p-6 mb-6 inline-block">
                <div className="text-5xl font-bold text-orange-600 tracking-widest font-mono">
                  {companyCode}
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={handleCopyCode}
                  variant="outline"
                  size="lg"
                  className="mr-3"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Code
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleContinue}
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                >
                  Continue to Dashboard
                </Button>
              </div>
              <p className="text-sm text-slate-600 mt-4">
                Share this code with your employees so they can join your company.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
      
      {/* Footer with Contact Information */}
      <footer className="mt-8 bg-slate-800 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">SafetyMind AI</span>
            </div>
            <div className="flex items-center gap-1">
              <span>Email:</span>
              <a href="mailto:support@safetymindai.com" className="text-orange-400 hover:text-orange-300">
                support@safetymindai.com
              </a>
            </div>
            <div className="flex items-center gap-1">
              <span>Phone:</span>
              <a href="tel:4199443998" className="text-orange-400 hover:text-orange-300">
                419-944-3998
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default CompanyRegister;
